import { useContext, useState } from "react";
import { AccessibilityContext } from "../contexts/AccessibilityContext";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import logo from "../assets/common/logo.png";

export const Footer: React.FC = () => {
    const { isContrast, setIsContrast } = useContext(AccessibilityContext);
    const { t } = useTranslation();
    const [revealedContacts, setRevealedContacts] = useState<Record<string, boolean>>({});

    const decodeContactValue = (encoded: string) => {
        if (typeof atob === "function") {
            return atob(encoded);
        }
        return encoded;
    };

    const formatPhoneNumber = (rawPhone: string) => {
        const normalized = rawPhone.replace(/[^+\d]/g, "");
        const match = normalized.match(/^(\+?\d{2})(\d{3})(\d{3})(\d{3})$/);

        if (match) {
            return `${match[1]}-${match[2]}-${match[3]}-${match[4]}`;
        }

        return rawPhone;
    };

    const buildPhoneHref = (rawPhone: string) => rawPhone.replace(/[^+\d]/g, "");

    const buildPhoneAriaLabel = (rawPhone: string) => formatPhoneNumber(rawPhone);

    const renderContact = (
        {
            id,
            label,
            phoneEncoded,
            emailEncoded
        }: { id: string; label: string; phoneEncoded?: string; emailEncoded?: string; }
    ) => {
        const isRevealed = revealedContacts[id];
        const linkClassName = `${isContrast ? "text-yellow-200 hover:text-yellow-100" : "text-cyan-700 hover:text-cyan-900"} font-medium`;
        const buttonClassName = `${isContrast ? "text-yellow-200 hover:text-yellow-100" : "text-cyan-700 hover:text-cyan-900"} font-semibold underline`;

        if (!isRevealed) {
            return (
                <button
                    type="button"
                    className={buttonClassName}
                    onClick={() => setRevealedContacts((prev) => ({ ...prev, [id]: true }))}
                    aria-label={`${t("Pokaż kontakt")}: ${label}`}
                >
                    {t("Pokaż kontakt")}
                </button>
            );
        }

        const phone = phoneEncoded ? decodeContactValue(phoneEncoded) : null;
        const email = emailEncoded ? decodeContactValue(emailEncoded) : null;

        return (
            <>
                {phone && (
                    <a
                        href={`tel:${buildPhoneHref(phone)}`}
                        aria-label={buildPhoneAriaLabel(phone)}
                        className={`${linkClassName} block`}
                    >
                        tel.: {formatPhoneNumber(phone)}
                    </a>
                )}
                {email && (
                    <a
                        href={`mailto:${email}`}
                        className={linkClassName}
                    >
                        {email}
                    </a>
                )}
            </>
        );
    };

    const primaryContacts = [
        {
            id: "management",
            label: t("Zarząd"),
            phoneEncoded: "KzQ4NTE1NDA3NDU3",
            emailEncoded: "emFyemFkQHRvdHVwb2ludC5wbA=="
        },
        {
            id: "orders",
            label: t("Zamówienia i wdrożenia"),
            phoneEncoded: "KzQ4Njk4NzA3NzM3",
            emailEncoded: "emFtb3dpZW5pYUB0b3R1cG9pbnQucGw="
        }
    ];

    const secondaryContacts = [
        {
            id: "consulting",
            label: t("Konsultacja opisów i audiodeskrypcji"),
            phoneEncoded: "KzQ4NTE2NzE1Njcz",
            emailEncoded: "YXVkaW9kZXNrcnlwY2phQHRvdHVwb2ludC5wbA=="
        },
        {
            id: "other",
            label: t("Inne sprawy"),
            emailEncoded: "Yml1cm9AdG90dXBvaW50LnBs"
        }
    ];

    const changeLanguage = (language: string) => {
        i18n.changeLanguage(language);
    };

    const isActive = (language: string) => {
        return i18n.language === language;
    };

    const handleNavigate = (sectionId: string) => {
        const section = document.getElementById(sectionId);
        if (section) {
            section.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <footer className="md:mt-auto bg-cyan-600 bg-opacity-10 footerContainer" role="contentinfo">
            <div className="md:w-[900px] w-[350px] ml-auto mr-auto py-6 md:py-10 mx-[10px]">
                {/* Logo i dane firmy - góra */}
                <div className="mb-6 md:mb-8">
                    <img
                        className="w-[180px] md:w-[200px] h-auto object-contain mb-4"
                        alt={t("Logo PIRS Creative Lab")}
                        src={logo}
                    />
                    <address className={`${isContrast ? "text-yellow-400" : "text-gray-700"} not-italic text-sm leading-relaxed`}>
                        <strong>PIRS Creative Lab Sp. z o.o.</strong><br />
                        ul. Twarda 44, 00-831 Warszawa<br />
                        NIP 9512364158 / REGON 146510222 / KRS 0000448627
                    </address>
                </div>

                {/* Sekcje w grid: Kontakt (2 kolumny), Menu, Aplikacja */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-4 mb-4 md:mb-6">
                    {/* Kontakt - kolumna 1 (Zarząd i Zamówienia) */}
                    <div className="md:col-span-1">
                        <h3 className={`${isContrast ? "text-yellow-400" : "text-gray-900"} font-bold text-lg mb-3 md:mb-4`}>
                            {t("Kontakt")}
                        </h3>
                        <div className={`${isContrast ? "text-yellow-400" : "text-gray-700"} text-sm space-y-2 md:space-y-3`}>
                            <p className="font-semibold">{t("Porozmawiajmy!")}</p>
                            {primaryContacts.map((contact) => (
                                <p key={contact.id}>
                                    <span className="block text-xs mb-1">{contact.label}:</span>
                                    {renderContact(contact)}
                                </p>
                            ))}
                        </div>
                    </div>

                    {/* Kontakt - kolumna 2 (Konsultacja i Inne sprawy) */}
                    <div className="md:col-span-1">
                        <h3 className={`${isContrast ? "text-yellow-400" : "text-gray-900"} font-bold text-lg mb-3 md:mb-4 invisible md:visible md:opacity-0`} aria-hidden="true">
                            {t("Kontakt")}
                        </h3>
                        <div className={`${isContrast ? "text-yellow-400" : "text-gray-700"} text-sm space-y-2 md:space-y-3 md:pt-[28px]`}>
                            {secondaryContacts.map((contact) => (
                                <p key={contact.id}>
                                    <span className="block text-xs mb-1">{contact.label}:</span>
                                    {renderContact(contact)}
                                </p>
                            ))}
                        </div>
                    </div>

                    {/* Menu */}
                    <div className="md:col-span-1">
                        <h3 className={`${isContrast ? "text-yellow-400" : "text-gray-900"} font-bold text-lg mb-3 md:mb-4`}>
                            {t("Menu")}
                        </h3>
                        <nav aria-label={t("Menu główne stopki")}>
                            <ul className={`${isContrast ? "text-yellow-400" : "text-gray-700"} text-sm space-y-1.5 md:space-y-2`}>
                                <li>
                                    <a
                                        href="#faq"
                                        onClick={(e) => { e.preventDefault(); handleNavigate('faq'); }}
                                        className={`${isContrast ? "hover:text-yellow-200" : "hover:text-cyan-700"} transition-colors`}
                                    >
                                        FAQ
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="#galeria"
                                        onClick={(e) => { e.preventDefault(); handleNavigate('galeria'); }}
                                        className={`${isContrast ? "hover:text-yellow-200" : "hover:text-cyan-700"} transition-colors`}
                                    >
                                        {t("Galeria")}
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="#gdziejestesmy"
                                        onClick={(e) => { e.preventDefault(); handleNavigate('gdziejestesmy'); }}
                                        className={`${isContrast ? "hover:text-yellow-200" : "hover:text-cyan-700"} transition-colors`}
                                    >
                                        TOTUPOINT {t("w liczbach")}
                                    </a>
                                </li>
                            </ul>
                        </nav>
                    </div>

                    {/* Aplikacja */}
                    <div className="md:col-span-1">
                        <h3 className={`${isContrast ? "text-yellow-400" : "text-gray-900"} font-bold text-lg mb-3 md:mb-4`}>
                            {t("Aplikacja")}
                        </h3>
                        <nav aria-label={t("Menu aplikacji")}>
                            <ul className={`${isContrast ? "text-yellow-400" : "text-gray-700"} text-sm space-y-1.5 md:space-y-2`}>
                                <li>
                                    <a
                                        href="/aplikacja"
                                        className={`${isContrast ? "hover:text-yellow-200" : "hover:text-cyan-700"} transition-colors`}
                                    >
                                        {t("Pobierz aplikację")}
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="/lokalizacje"
                                        className={`${isContrast ? "hover:text-yellow-200" : "hover:text-cyan-700"} transition-colors`}
                                    >
                                        {t("Lokalizacje")}
                                    </a>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>

                {/* Separator */}
                <div className={`border-t ${isContrast ? "border-yellow-400" : "border-gray-300"} my-4 md:my-6`}></div>

                {/* Dolna sekcja - Copyright i Media społecznościowe */}
                <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                    {/* Copyright */}
                    <p className={`${isContrast ? "text-yellow-400" : "text-gray-600"} text-sm`}>
                        © Copyright PIRS 2025
                    </p>

                    {/* Media społecznościowe i ustawienia */}
                    <div className="flex items-center gap-4">
                        <span className={`${isContrast ? "text-yellow-400" : "text-gray-700"} text-sm hidden sm:block`}>
                            {t("Media społecznościowe")}
                        </span>

                        {/* Przełącznik kontrastu */}
                        <button
                            onClick={() => setIsContrast(!isContrast)}
                            className="cursor-pointer p-1 hover:opacity-80 transition-opacity"
                            aria-label={t("Zmień kontrast")}
                            title={t("Zmień kontrast")}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 134.42 134.42"
                                className="w-6 h-6"
                            >
                                <defs>
                                    <style>
                                        {`.cls-1{fill:${isContrast ? "rgb(250 204 21)" : "black"};stroke:${isContrast ? "rgb(250 204 21)" : "black"};stroke-miterlimit:10;stroke-width:4px;}`}
                                        {`.cls-2{fill:none;stroke-width:0px;}`}
                                    </style>
                                </defs>
                                <g>
                                    <rect className="cls-2" width="134.42" height="134.42" />
                                    <path
                                        className="cls-1"
                                        d="M67.21,22.25c-24.83,0-44.96,20.13-44.96,44.96s20.13,44.96,44.96,44.96,44.96-20.13,44.96-44.96-20.13-44.96-44.96-44.96ZM67.21,110.43V23.99c23.87,0,43.22,19.35,43.22,43.22s-19.35,43.22-43.22,43.22Z"
                                    />
                                </g>
                            </svg>
                        </button>

                        {/* Przełącznik języka */}
                        <div className="flex items-center gap-1 text-sm border rounded-md px-2 py-1" style={{borderColor: isContrast ? "rgb(250 204 21)" : "#ccc"}}>
                            {isActive('pl') ? (
                                <span className={`font-bold px-1 ${isContrast ? 'text-yellow-400' : 'text-sky-950'}`} aria-label={t('Polska wersja aktywna')}>PL</span>
                            ) : (
                                <button
                                    aria-label={t('Przełącz na Polski')}
                                    className={`px-1 ${isContrast ? 'text-white hover:text-yellow-400' : 'text-gray-600 hover:text-sky-950'} transition-colors`}
                                    onClick={() => changeLanguage("pl")}
                                >
                                    PL
                                </button>
                            )}
                            <span className={`${isContrast ? 'text-white' : 'text-gray-400'}`}>|</span>
                            {isActive('en') ? (
                                <span className={`font-bold px-1 ${isContrast ? 'text-yellow-400' : 'text-sky-950'}`} aria-label={t('Angielska wersja aktywna')}>ANG</span>
                            ) : (
                                <button
                                    aria-label="Change to English"
                                    className={`px-1 ${isContrast ? 'text-white hover:text-yellow-400' : 'text-gray-600 hover:text-sky-950'} transition-colors`}
                                    onClick={() => changeLanguage("en")}
                                >
                                    ANG
                                </button>
                            )}
                        </div>

                        {/* Social Media Icons */}
                        <a
                            href="https://www.facebook.com/profile.php?id=100064351974958"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${isContrast ? "text-yellow-400 hover:text-yellow-200" : "text-gray-700 hover:text-cyan-700"} transition-colors`}
                            aria-label={t("Odwiedź naszą stronę na Facebooku")}
                        >
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                            </svg>
                        </a>
                        <a
                            href="https://www.linkedin.com/company/totupoint"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${isContrast ? "text-yellow-400 hover:text-yellow-200" : "text-gray-700 hover:text-cyan-700"} transition-colors`}
                            aria-label={t("Odwiedź naszą stronę na LinkedIn")}
                        >
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                            </svg>
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
};
